/**
 * 画面上部の帯: 時点（過去を見ているときだけ）／オフライン／新しい版。
 */
import { useEffect, useRef, useState } from "react";
import { useI18n, formatDateTime } from "../useI18n.js";
import { useNow, useOnline, useViewpoint } from "../hooks.js";
import { pastViewpoint, viewpoint } from "../viewpoint.js";
import { onAppEvent } from "../api.js";

/**
 * 時点の帯。このシステムでひとつだけ目立たせるもの。
 * 「2027-01-31 時点の記録を表示しています（最新に戻る）」を常に上部に固定する。最新なら何も出さない。
 */
/**
 * M-07: 選んだ過去の日付（asOf）が効くのは台帳・証跡の画面だけ。ほかの画面では asOf の帯は出さない。
 * review-4 M3: ただし固定した知識時点（knownAt）が古くなったときは、**どの画面でも**帯と「最新に戻る」を出す
 * （GET には固定した knownAt が載るので、どの画面でも新しい記録が見えなくなり得る）。
 */
const AS_OF_ROUTES = new Set(["list", "register", "criteria"]);

export function AsOfBand({ onPastChange, routeName }: { onPastChange: (past: boolean) => void; routeName: string }) {
  const { t, lang } = useI18n();
  const s = useViewpoint();
  const now = useNow();
  const past = s.asOf && AS_OF_ROUTES.has(routeName)
    ? pastViewpoint(s, now)
    : pastViewpoint({ ...s, asOf: null }, now);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => { onPastChange(past !== null); }, [past !== null, onPastChange]);   // eslint-disable-line react-hooks/exhaustive-deps

  // 帯の高さを CSS 変数にして、上部に固定した見出しが帯の下に来るようにする
  useEffect(() => {
    const el = ref.current;
    const root = document.documentElement;
    if (!el) { root.style.setProperty("--band-h", "0px"); return; }
    const ro = new ResizeObserver(() => root.style.setProperty("--band-h", `${el.offsetHeight}px`));
    ro.observe(el);
    return () => { ro.disconnect(); root.style.setProperty("--band-h", "0px"); };
  }, [past !== null]);   // eslint-disable-line react-hooks/exhaustive-deps

  if (!past) return null;
  const text = past.kind === "asOf"
    ? t("asof.band.date", { date: past.date })
    : t("asof.band.time", { time: formatDateTime(past.at, lang) });
  return (
    <div className="asof-band" ref={ref} role="region" aria-label={t("asof.label")}>
      <p>{text}</p>
      <button type="button" onClick={() => viewpoint.reset()}>{t("asof.backToLatest")}</button>
    </div>
  );
}

/** サーバが返す「復旧の操作の後の最初のログイン」の知らせ（review-4 M2-c）。 */
export interface RecoveryNotice { kind: "UNLOCK" | "PASSWORD_RESET" | "TOTP_RESET"; actorName: string; at: string }

/**
 * 復旧の操作（ロック解除・パスワードの再設定・認証アプリのリセット）の後の最初のログインで、一度だけ出す。
 * サーバはこの1回で知らせを消すので、閉じたらもう出ない。心当たりが無ければ連絡するよう促す。
 */
export function RecoveryNoticeBanner({ notice, onDismiss }: { notice: RecoveryNotice; onDismiss: () => void }) {
  const { t, lang } = useI18n();
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => { ref.current?.focus(); }, []);
  const time = formatDateTime(notice.at, lang);
  const text = notice.actorName
    ? t(`rnotice.${notice.kind}`, { name: notice.actorName, time })
    : t("rnotice.serverSide", { time });
  return (
    <div className="callout warn rnotice" role="alert" tabIndex={-1} ref={ref}>
      <p className="bold">{t("rnotice.title")}</p>
      <p>{text}</p>
      <div className="cluster">
        <button type="button" onClick={onDismiss}>{t("rnotice.ok")}</button>
      </div>
    </div>
  );
}

/** オフライン中は画面上部に小さく出す。 */
export function OfflineBanner() {
  const { t } = useI18n();
  const online = useOnline();
  if (online) return null;
  return (
    <div className="sys-banner offline" role="status">
      <p>{t("sys.offline")}</p>
    </div>
  );
}

/** 新しい版があります（再読み込み）。控えめに出す。 */
export function UpdateBanner() {
  const { t } = useI18n();
  const [shown, setShown] = useState(false);
  useEffect(() => onAppEvent((ev) => { if (ev === "newVersion") setShown(true); }), []);
  if (!shown) return null;
  return (
    <div className="sys-banner" role="status">
      <p>{t("sys.newVersion")}</p>
      <button type="button" className="primary" onClick={() => location.reload()}>{t("sys.reload")}</button>
      <button type="button" className="quiet" onClick={() => setShown(false)}>{t("sys.later")}</button>
    </div>
  );
}
